(() => {
  const P = () => window.GhaliaPricing;
  const moneyNet = (cents) => P()?.formatDisplay(cents) ?? (Number(cents) / 100).toFixed(2);
  const moneyDisplay = (cents) => P()?.formatDisplay(cents) ?? (Number(cents) / 100).toFixed(2);

  const isFreeGiftLine = (item) => item?.properties?._ghalia_free_gift === 'true';
  const isBundleLine = (item) => Boolean(item?.properties?._ghalia_stack_anchor);

  const NATIVE_BUNDLE_HANDLES =
    window.GhaliaNativeBundleHandles ||
    new Set([
      'ghalia-infinity-ring-duo',
      'knot-bridge-duo-silver',
      'knot-bridge-duo-gold',
      'ghalia-knot-hero-set-gold',
      'ghalia-knot-hero-set-silver',
      'ghalia-knot-two-tone-ring-stack',
      'ghalia-knot-wrist-story',
      'ghalia-pave-gold-stack',
      'ghalia-pave-silver-stack',
      'ghalia-cross-ring-earring-gold',
    ]);

  const isNativeBundleLine = (item) => NATIVE_BUNDLE_HANDLES.has(item?.handle);

  const nativeBundleDiscountPct = (handle) => {
    const map = window.GhaliaNativeBundleDiscounts || {};
    const pct = Number(map[handle]);
    return pct > 0 ? pct : 0;
  };

  const PROMO_STORAGE_KEY = 'ghalia_cart_promo_code';

  const storedPromoCode = () => {
    try {
      return sessionStorage.getItem(PROMO_STORAGE_KEY) || '';
    } catch (_) {
      return '';
    }
  };

  const cartPromoApp = (cart) =>
    (cart?.cart_level_discount_applications || []).find((d) => d?.type === 'discount_code');

  const hasPromoCode = (cart) => {
    if (cartPromoApp(cart)) return true;
    if (Number(cart?.total_discount) > 0) return true;
    return (cart?.items || []).some(
      (item) => !isNativeBundleLine(item) && (item.line_level_discount_allocations || []).length > 0
    );
  };

  const lineOriginalNet = (item, row) => {
    const qty = Number(item.quantity) || 1;
    const origLine = Number(item.original_line_price) || Number(item.line_price) || 0;
    if (origLine > 0) return origLine;
    const domNet = Number(row?.dataset?.cartLineNet || 0);
    const domQty = Number(row?.querySelector('[data-cart-line-qty]')?.value) || qty;
    if (domNet > 0 && domQty > 0) return Math.round(domNet * (qty / domQty));
    if (domNet > 0) return domNet;
    const unit = Number(item.original_price ?? item.price ?? 0);
    if (unit > 0) return unit * qty;
    return Number(item.line_price) || 0;
  };

  const lineFinalNet = (item) =>
    Number(item.final_line_price) || Number(item.line_price) || lineOriginalNet(item);

  const setCustomerLinePrice = (currentEl, pricing, netCents, row) => {
    if (!currentEl || !pricing) return;
    const next = pricing.formatCustomerLineNet
      ? pricing.formatCustomerLineNet(netCents, row)
      : pricing.formatNet(netCents);
    if (currentEl.textContent.trim() !== next.trim()) currentEl.textContent = next;
  };

  const applyCodePromoLinePrice = (item, row, pricing, priceEl, compareEl, currentEl) => {
    const originalNet = lineOriginalNet(item, row);
    const finalNet = lineFinalNet(item);
    const allocNet = (item.line_level_discount_allocations || []).reduce(
      (sum, alloc) => sum + (Number(alloc.amount) || 0),
      0
    );
    const currentNet = allocNet > 0 ? Math.max(0, originalNet - allocNet) : finalNet;
    if (originalNet <= 0 || currentNet <= 0 || currentNet >= originalNet) return false;

    currentEl.textContent = pricing.formatCustomerLineNet
      ? pricing.formatCustomerLineNet(currentNet, row)
      : pricing.formatNet(currentNet);
    if (compareEl) {
      compareEl.textContent = pricing.formatCustomerLineNet
        ? pricing.formatCustomerLineNet(originalNet, row)
        : pricing.formatNet(originalNet);
      compareEl.hidden = false;
      compareEl.removeAttribute('hidden');
    }
    priceEl.classList.remove('is-stack-discounted');
    priceEl.classList.add('is-promo-discounted');
    return true;
  };

  const cartHasNativeBundle = (cart) =>
    (cart?.items || []).some((item) => isNativeBundleLine(item));

  const parseStacksFromCart = (cart) => {
    const stacks = new Map();
    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item)) return;
      const anchor = item.properties?._ghalia_stack_anchor;
      const pct = parseInt(item.properties?._ghalia_stack_pct, 10) || 0;
      const membersRaw = item.properties?._ghalia_stack_members || '';
      if (!anchor || !pct || !membersRaw) return;
      const members = membersRaw.split(',').map((h) => h.trim()).filter(Boolean);
      if (!members.length) return;
      if (!stacks.has(anchor)) stacks.set(anchor, { anchor, pct, members });
    });
    return [...stacks.values()];
  };

  const handlesInCart = (cart) => {
    const set = new Set();
    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item)) return;
      if (item.handle) set.add(item.handle);
    });
    return set;
  };

  const isStackComplete = (cart, stack) => {
    const present = handlesInCart(cart);
    return stack.members.every((handle) => present.has(handle));
  };

  const qtyByHandle = (cart) => {
    const map = new Map();
    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item)) return;
      if (!item.handle) return;
      map.set(item.handle, (map.get(item.handle) || 0) + (Number(item.quantity) || 1));
    });
    return map;
  };

  const buildBundleUnitsByLineKey = (cart, stacks) => {
    const perHandle = discountedQtyByHandle(cart, stacks);
    const remaining = new Map(perHandle);
    const byKey = new Map();
    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item) || !item.handle) return;
      const left = remaining.get(item.handle) || 0;
      const qty = Number(item.quantity) || 1;
      const setUnits = Math.min(qty, left);
      byKey.set(item.key, setUnits);
      remaining.set(item.handle, left - setUnits);
    });
    return byKey;
  };

  /** Bundle discount applies to min qty across set members (1 set = 1 unit each). */
  const discountedQtyByHandle = (cart, stacks) => {
    const qty = qtyByHandle(cart);
    const discounted = new Map();
    stacks.forEach((stack) => {
      if (!isStackComplete(cart, stack)) return;
      const setCount = Math.min(...stack.members.map((h) => qty.get(h) || 0));
      if (setCount <= 0) return;
      stack.members.forEach((h) => {
        discounted.set(h, Math.max(discounted.get(h) || 0, setCount));
      });
    });
    return discounted;
  };

  const lineNetCents = (cart, drawer, item, pricing) => {
    const row = drawer?.querySelector(`[data-cart-line-key="${item.key}"]`);
    const fromDom = Number(row?.dataset?.cartLineNet || 0);
    if (fromDom > 0) return fromDom;
    return (
      Number(item.original_line_price) ||
      Number(item.line_price) ||
      pricing.cartLineAmounts(item, row).net
    );
  };

  const lineUnitNet = (item, row, pricing) => {
    const qty = Number(item.quantity) || 1;
    const { net, unitNet } = pricing.cartLineAmounts(item, row);
    if (unitNet > 0) return unitNet;
    return Math.round(net / qty);
  };

  const computeNetSubtotal = (cart, drawer, pricing) => {
    let netSubtotalCents = 0;
    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item)) return;
      netSubtotalCents += lineNetCents(cart, drawer, item, pricing);
    });
    return netSubtotalCents;
  };

  /** Ti Sento: read stack line properties, apply % when all members are in cart. */
  const computeStackState = (cart, drawer) => {
    const pricing = P();
    if (!pricing) return { qualified: false };

    const netSubtotalCents = computeNetSubtotal(cart, drawer, pricing);
    const stacks = parseStacksFromCart(cart).filter((stack) => isStackComplete(cart, stack));

    const state = {
      qualified: false,
      netSubtotalCents,
      savingsNetCents: 0,
      bundleSavingsNetCents: 0,
      pct: 0,
      memberSet: null,
      discountedQty: new Map(),
    };

    if (!stacks.length) return state;

    if (cartHasNativeBundle(cart)) {
      state.netSubtotalCents = netSubtotalCents;
      state.pct = 8;
      return state;
    }

    const memberSet = new Set();
    stacks.forEach((stack) => stack.members.forEach((h) => memberSet.add(h)));
    const pct = Math.max(...stacks.map((s) => s.pct));
    const discountedQty = discountedQtyByHandle(cart, stacks);
    const bundleUnitsByLine =
      pricing.bundleUnitsByLineKey?.(cart) ?? buildBundleUnitsByLineKey(cart, stacks);

    let eligibleNetCents = 0;
    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item)) return;
      const setUnits = bundleUnitsByLine.get(item.key) || 0;
      if (setUnits <= 0) return;
      const row = drawer?.querySelector(`[data-cart-line-key="${item.key}"]`);
      const unitNet = lineUnitNet(item, row, pricing);
      eligibleNetCents += unitNet * setUnits;
    });

    const themeSavingsNetCents = Math.round((eligibleNetCents * pct) / 100);

    state.memberSet = memberSet;
    state.discountedQty = discountedQty;
    state.bundleUnitsByLine = bundleUnitsByLine;
    state.pct = pct;
    state.qualified = themeSavingsNetCents > 0;
    state.bundleSavingsNetCents = themeSavingsNetCents;
    state.savingsNetCents = themeSavingsNetCents;

    return state;
  };

  const applyLinePrices = (cart, state, drawer) => {
    const pricing = P();
    if (!pricing || !drawer) return;

    (cart?.items || []).forEach((item) => {
      if (isFreeGiftLine(item)) return;
      const row = drawer.querySelector(`[data-cart-line-key="${item.key}"]`);
      if (!row) return;
      const priceEl = row.querySelector('[data-cart-line-price]');
      const compareEl = row.querySelector('[data-cart-line-compare]');
      const currentEl = row.querySelector('[data-cart-line-current]');
      if (!priceEl || !currentEl) return;

      if (isNativeBundleLine(item)) {
        const finalNet = Number(item.final_line_price) || lineFinalNet(item);
        const compareDisplayFromDom = Number(row?.dataset?.cartLineCompareDisplay || 0);
        let compareDisplay = 0;

        if (compareDisplayFromDom > pricing.displayCents(finalNet)) {
          compareDisplay = compareDisplayFromDom;
        } else if (Number(item.original_line_price) > finalNet) {
          compareDisplay = pricing.displayCents(Number(item.original_line_price));
        } else {
          const pct = nativeBundleDiscountPct(item.handle) || state.pct || 8;
          const compareNet = Math.round((finalNet * 100) / (100 - pct));
          compareDisplay = pricing.displayCents(compareNet);
        }

        setCustomerLinePrice(currentEl, pricing, finalNet, row);
        if (compareEl && compareDisplay > pricing.displayCents(finalNet)) {
          compareEl.textContent = pricing.formatDisplay(compareDisplay);
          compareEl.hidden = false;
          compareEl.removeAttribute('hidden');
        } else if (compareEl) {
          compareEl.hidden = true;
        }
        priceEl.classList.remove('is-stack-discounted', 'is-promo-discounted');
        return;
      }

      const allocNet = (item.line_level_discount_allocations || []).reduce(
        (sum, alloc) => sum + (Number(alloc.amount) || 0),
        0
      );
      const originalNet = lineOriginalNet(item, row);
      const finalNet = lineFinalNet(item);
      const hasLinePromoPrice =
        allocNet > 0 || (originalNet > 0 && finalNet > 0 && finalNet < originalNet);
      if (hasLinePromoPrice && !isNativeBundleLine(item)) {
        if (applyCodePromoLinePrice(item, row, pricing, priceEl, compareEl, currentEl)) return;
      }

      const lineNet = lineFinalNet(item);
      const qty = Number(item.quantity) || 1;
      const unitNet = lineUnitNet(item, row, pricing);
      let currentNet = lineNet;
      let compareNet = 0;

      if (state.qualified && state.memberSet?.has(item.handle)) {
        const pct = state.pct || 0;
        const discountedUnits = state.bundleUnitsByLine?.get(item.key) || 0;
        const regularUnits = qty - discountedUnits;

        if (discountedUnits > 0) {
          const discountedUnitNet = pricing.applyDiscount(unitNet, pct);
          currentNet = unitNet * regularUnits + discountedUnitNet * discountedUnits;
          compareNet = unitNet * qty;
        }
      }

      setCustomerLinePrice(currentEl, pricing, currentNet, row);
      if (compareEl) {
        if (compareNet > currentNet) {
          setCustomerLinePrice(compareEl, pricing, compareNet, row);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }
      priceEl.classList.remove('is-promo-discounted');
      priceEl.classList.toggle('is-stack-discounted', Boolean(compareNet > currentNet));
    });
  };

  const shippingNetCents = (drawer) => {
    const summary = drawer?.querySelector('[data-cart-summary]');
    if (summary && summary.dataset.shippingNetCents != null && summary.dataset.shippingNetCents !== '') {
      return Number(summary.dataset.shippingNetCents) || 0;
    }
    const shippingRow = drawer?.querySelector('[data-cart-summary-shipping]');
    const shippingText = shippingRow?.textContent?.trim() || '';
    if (!shippingText || /free/i.test(shippingText)) return 0;
    const digits = shippingText.replace(/[^\d.,]/g, '');
    if (!digits) return 0;
    return Math.round(parseFloat(digits.replace(',', '')) * 100) || 0;
  };

  const applySummary = (cart, state, drawer) => {
    const stackRow = drawer.querySelector('[data-cart-stack-discount-row]');
    const stackAmount = drawer.querySelector('[data-cart-stack-discount-amount]');
    const stackLabel = drawer.querySelector('[data-cart-stack-discount-label]');

    const bundleSavings = state.bundleSavingsNetCents || 0;
    const showStackDiscount = state.qualified && bundleSavings > 0 && state.pct > 0;

    if (stackRow && stackAmount && stackLabel) {
      if (showStackDiscount) {
        stackRow.hidden = false;
        stackLabel.textContent =
          stackLabel.dataset.template?.replace('__PCT__', String(state.pct)) ||
          `Complete the look (${state.pct}% off)`;
        stackAmount.textContent = `-${moneyNet(bundleSavings)}`;
      } else {
        stackRow.hidden = true;
        stackLabel.textContent = '';
        stackAmount.textContent = '';
      }
    }
  };

  const INFINITY_SET_MEMBERS = ['infinity-bridge-ring', 'single-infinity-pave-ring'];

  const updateBundleSkuHint = (cart, drawer) => {
    const hint = drawer?.querySelector('[data-cart-bundle-sku-hint]');
    if (!hint) return;
    if (cartHasNativeBundle(cart)) {
      hint.hidden = true;
      return;
    }
    const handles = handlesInCart(cart);
    const hasSetMembers = INFINITY_SET_MEMBERS.every((h) => handles.has(h));
    hint.hidden = !hasSetMembers;
  };

  const loadCart = async (cart, forceFetch = false) => {
    if (!forceFetch && cart?.items?.length) return cart;
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      return await res.json();
    } catch {
      return cart?.items ? cart : null;
    }
  };

  const cartFingerprint = (cart) => {
    if (!cart?.items?.length) return `empty:${cart?.item_count || 0}`;
    return cart.items
      .map(
        (item) =>
          `${item.key}:${item.quantity}:${item.final_line_price || item.line_price || 0}:${item.total_discount || 0}`
      )
      .join('|');
  };

  let lastStackFingerprint = '';
  let pricingApplyTimer = null;
  let pricingApplyInFlight = false;
  let pendingPricingCart = null;

  const apply = async (cart, options = {}) => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const data = await loadCart(cart, options.forceFetch);
    if (!data?.items) return;
    const fingerprint = cartFingerprint(data);
    if (!options.forceFetch && fingerprint === lastStackFingerprint) return;
    lastStackFingerprint = fingerprint;
    const state = computeStackState(data, drawer);
    updateBundleSkuHint(data, drawer);
    applyLinePrices(data, state, drawer);
    applySummary(data, state, drawer);
    window.GhaliaCart = window.GhaliaCart || {};
    window.GhaliaCart.lastStackState = state;
    document.dispatchEvent(new CustomEvent('ghalia:cart:stack-discount', { detail: { cart: data, state } }));
  };

  const scheduleApply = (cart, options = {}) => {
    if (cart) pendingPricingCart = cart;
    if (pricingApplyTimer) cancelAnimationFrame(pricingApplyTimer);
    pricingApplyTimer = requestAnimationFrame(() => {
      pricingApplyTimer = null;
      if (pricingApplyInFlight) return;
      const nextCart = pendingPricingCart;
      pendingPricingCart = null;
      if (!nextCart) return;
      pricingApplyInFlight = true;
      apply(nextCart, options).finally(() => {
        pricingApplyInFlight = false;
        if (pendingPricingCart) scheduleApply(pendingPricingCart, options);
      });
    });
  };

  const bind = () => {
    document.addEventListener('ghalia:cart:pricing', (e) => scheduleApply(e.detail?.cart));
    document.addEventListener('ghalia:cart-drawer:refreshed', () => {
      lastStackFingerprint = '';
      scheduleApply(window.GhaliaCart?.lastCart);
    });
    apply();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind);
  else bind();
})();
